import {Application} from 'express';
import swaggerJSDoc from 'swagger-jsdoc';
import swaggerUi from 'swagger-ui-express';
import {DocsOptions} from "../middleware/types";

/**
 * Sets up Swagger/OpenAPI documentation for the Express application
 */
export function setupSwaggerDocs(app: Application, options: DocsOptions): void {
  const swaggerDefinition = {
    openapi: '3.0.0',
    info: {
      title: options.title || 'API Documentation',
      version: options.version || '1.0.0',
      description: options.description || 'API Documentation generated by express-api-framework',
    },
    servers: [
      {
        url: '/',
        description: 'Current server',
      },
    ],
    components: {
      securitySchemes: {
        bearerAuth: {
          type: 'http',
          scheme: 'bearer',
          bearerFormat: 'JWT',
        },
      },
      schemas: {
        Error: {
          type: 'object',
          properties: {
            success: {
              type: 'boolean',
              example: false,
            },
            message: {
              type: 'string',
              example: 'An error occurred',
            },
            code: {
              type: 'string',
              example: 'INTERNAL_ERROR',
            },
            correlationId: {
              type: 'string',
              example: '550e8400-e29b-41d4-a716-446655440000',
            },
          },
        },
        SuccessResponse: {
          type: 'object',
          properties: {
            success: {
              type: 'boolean',
              example: true,
            },
            message: {
              type: 'string',
              example: 'Operation successful',
            },
            data: {
              type: 'object',
              example: {},
            },
            correlationId: {
              type: 'string',
              example: '550e8400-e29b-41d4-a716-446655440000',
            },
          },
        },
        PaginatedResponse: {
          type: 'object',
          properties: {
            success: {
              type: 'boolean',
              example: true,
            },
            message: {
              type: 'string',
              example: 'Operation successful',
            },
            data: {
              type: 'array',
              items: {
                type: 'object',
              },
            },
            pagination: {
              type: 'object',
              properties: {
                page: {
                  type: 'number',
                  example: 1,
                },
                limit: {
                  type: 'number',
                  example: 10,
                },
                totalPages: {
                  type: 'number',
                  example: 5,
                },
                totalItems: {
                  type: 'number',
                  example: 47,
                },
              },
            },
            correlationId: {
              type: 'string',
              example: '550e8400-e29b-41d4-a716-446655440000',
            },
          },
        },
      },
      responses: {
        BadRequest: {
          description: 'Bad Request',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/Error',
              },
            },
          },
        },
        Unauthorized: {
          description: 'Unauthorized',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/Error',
              },
            },
          },
        },
        Forbidden: {
          description: 'Forbidden',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/Error',
              },
            },
          },
        },
        NotFound: {
          description: 'Not Found',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/Error',
              },
            },
          },
        },
        ServerError: {
          description: 'Internal Server Error',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/Error',
              },
            },
          },
        },
        SuccessResponse: {
          description: 'Success Response',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/SuccessResponse',
              },
            },
          },
        },
        PaginatedResponse: {
          description: 'Paginated Response',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/PaginatedResponse',
              },
            },
          },
        },
      },
    },
  };

  const swaggerOptions = {
    swaggerDefinition,
    apis: [
      // Default paths to look for JSDoc annotations
      './src/routes/**/*.ts',
      './src/controllers/**/*.ts',
      './dist/routes/**/*.js',
      './dist/controllers/**/*.js',
    ],
  };

  const swaggerSpec = swaggerJSDoc(swaggerOptions);

  // Serve swagger docs
  app.use(
    options.endpoint || '/api-docs',
    ...swaggerUi.serve,  // Spread the array
    swaggerUi.setup(swaggerSpec, {
      explorer: true,
      customCss: '.swagger-ui .topbar { display: none }',
    })
  );

  // Serve swagger spec as JSON
  app.get('/swagger.json', (req, res) => {
    res.setHeader('Content-Type', 'application/json');
    res.send(swaggerSpec);
  });
}

/**
 * Helper to generate route documentation from a schema
 */
export function generateRouteDoc(options: {
  tags: string[];
  summary: string;
  description?: string;
  operationId?: string;
  requestBody?: any;
  parameters?: any[];
  responses?: any;
  security?: any[];
}) {
  return options;
}
